import React, { useEffect, useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import { ShiftRecord } from '../types';
import { formatDateTime } from '../services/datetime';
import { tenderTotals } from '../services/payments';
import { printReportAuto } from '../services/receipt';
import './Sections.css';
import './Shifts.css';

const isToday = (iso: string) => {
  const d = new Date(iso);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};

// Matches shifts_fragment_layout.xml — store/branch header + payment-mode breakup + shift list.
const Shifts: React.FC = () => {
  const { sales, formatMoney, config, activeShift } = usePos();
  const [shifts, setShifts] = useState<ShiftRecord[]>([]);
  useEffect(() => {
    db.getShifts().then((s) => setShifts([...s].reverse()));
  }, []);

  const todays = useMemo(() => sales.filter((s) => s.status === 'completed' && isToday(s.timestamp)), [sales]);
  const totals = useMemo(() => tenderTotals(todays), [todays]);

  const modes = [
    { label: 'CASH', value: totals.cash },
    { label: 'CREDIT', value: totals.credit },
    { label: 'DIGITAL', value: totals.digital },
    { label: 'CARD', value: totals.card },
    { label: 'CHEQUE', value: 0 },
    { label: 'GOOGLE PAY', value: 0 },
  ];

  // Print the closed-shifts table to a thermal printer (ESC/POS), falling back
  // to the browser print dialog.
  const printUsb = () => {
    const rows: (string | number)[][] = shifts.map((s) => [
      s.dayId ? `${s.dayId} #${s.shiftNo ?? '-'}` : '-',
      s.billCount,
      formatMoney(s.cashSales),
      formatMoney(s.totalSales),
      formatMoney(s.expectedCash),
    ]);
    printReportAuto('Shifts', config.storeName, ['Shift', 'Bills', 'Cash', 'Total', 'Exp.Cash'], rows);
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>SHIFTS</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-grey" onClick={() => window.print()}>
            Print
          </button>
          <button className="btn-teal" onClick={printUsb}>
            🖨 USB Print
          </button>
        </div>
      </div>

      <div className="section-card">
        <div className="sh-head">
          <div>
            <div><strong>Store :</strong> {config.storeName}</div>
            <div><strong>Branch :</strong> {config.address || 'Main'}</div>
          </div>
          <div className="right">
            {activeShift ? (
              <>
                <div><strong>Open Shift :</strong> #{activeShift.shiftNo} ({activeShift.dayId})</div>
                <div><strong>Shift Start :</strong> {formatDateTime(activeShift.openedAt)}</div>
              </>
            ) : (
              <div><strong>No shift open</strong> — open one from Shift End</div>
            )}
            <div><strong>Now :</strong> {formatDateTime(new Date())}</div>
          </div>
        </div>

        <div className="sh-modes-title">Payment Mode</div>
        <div className="sh-modes">
          {modes.map((m) => (
            <div className="sh-mode" key={m.label}>
              <span className="sh-mode-lbl">{m.label} :</span>
              <span className="sh-mode-val">{formatMoney(m.value)}</span>
            </div>
          ))}
        </div>
        <div className="totals-line">Total : {formatMoney(todays.reduce((a, s) => a + s.total, 0))}</div>
      </div>

      <div className="section-card">
        <h2>Closed Shifts ({shifts.length})</h2>
        {shifts.length === 0 ? (
          <p className="muted">No closed shifts yet — close one from Shift End.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr>
                <th>Shift</th>
                <th>Closed At</th>
                <th className="num-cell">Bills</th>
                <th className="right">Cash</th>
                <th className="right">Card</th>
                <th className="right">Digital</th>
                <th className="right">Expenses</th>
                <th className="right">Total</th>
                <th className="right">Expected Cash</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s) => (
                <tr key={s.id}>
                  <td>{s.dayId ? `${s.dayId} #${s.shiftNo ?? '-'}` : '—'}</td>
                  <td>{formatDateTime(s.closedAt)}</td>
                  <td>{s.billCount}</td>
                  <td className="right">{formatMoney(s.cashSales)}</td>
                  <td className="right">{formatMoney(s.cardSales)}</td>
                  <td className="right">{formatMoney(s.digitalSales)}</td>
                  <td className="right">{formatMoney(s.expenses)}</td>
                  <td className="right">{formatMoney(s.totalSales)}</td>
                  <td className="right" style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
                    {formatMoney(s.expectedCash)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Shifts;
